/**
 * FlowComponent - Base class for all WhatsApp Flow components
 *
 * Each component type extends this class to provide:
 * - Default properties
 * - Property schema and validation
 * - Preview rendering (canvas)
 * - Property editor rendering (properties panel)
 * - WhatsApp JSON transformation
 * - Lifecycle hooks
 */

export class FlowComponent {
  constructor(config) {
    // Component metadata
    this.type = config.type;                    // 'TextHeading', 'TextInput', etc.
    this.name = config.name;                    // Display name
    this.icon = config.icon;                    // Icon for palette
    this.description = config.description || ''; // One-line, user-facing description for the palette
    this.category = config.category;            // 'display', 'input', 'selection', 'action'
    this.maxPerScreen = config.maxPerScreen;    // Max instances per screen (null = unlimited)

    // Component implementation functions
    this._getDefaultProperties = config.getDefaultProperties;
    this._getPropertySchema = config.getPropertySchema;
    this._validateProperties = config.validateProperties;
    this._renderPreview = config.renderPreview;
    this._renderPropertyEditor = config.renderPropertyEditor;
    this._transformToWhatsApp = config.transformToWhatsApp;
    this._transformFromWhatsApp = config.transformFromWhatsApp;

    // Optional lifecycle hooks
    this._onAdd = config.onAdd;
    this._onRemove = config.onRemove;
    this._onPropertyChange = config.onPropertyChange;
  }

  /**
   * Get default properties when component is added to canvas
   * @returns {Object} Default property values
   */
  getDefaultProperties() {
    if (this._getDefaultProperties) {
      return this._getDefaultProperties();
    }
    return {};
  }

  /**
   * Get property schema for validation and form generation
   * @returns {Object} Property schema definition
   */
  getPropertySchema() {
    if (this._getPropertySchema) {
      return this._getPropertySchema();
    }
    return {};
  }

  /**
   * Validate component properties
   * @param {Object} properties - Component properties to validate
   * @param {Object} context - Additional context (screen, other components, etc.)
   * @returns {Object} {errors: string[], warnings: string[]}
   */
  validateProperties(properties, context = {}) {
    if (this._validateProperties) {
      return this._validateProperties(properties, context);
    }
    return { errors: [], warnings: [] };
  }

  /**
   * Render component preview in canvas
   * @param {Object} properties - Component properties
   * @param {Object} context - Additional context
   * @returns {React.Component} Preview component
   */
  renderPreview(properties, context = {}) {
    if (this._renderPreview) {
      return this._renderPreview(properties, context);
    }
    return null;
  }

  /**
   * Render property editor in properties panel
   * @param {Object} properties - Current property values
   * @param {Function} onChange - Callback to update properties (propertyName, value)
   * @param {Object} context - Additional context
   * @returns {React.Component} Property editor component
   */
  renderPropertyEditor(properties, onChange, context = {}) {
    if (this._renderPropertyEditor) {
      return this._renderPropertyEditor(properties, onChange, context);
    }
    return null;
  }

  /**
   * Transform internal format to WhatsApp Flow JSON format
   * @param {Object} properties - Internal component properties
   * @param {Object} context - Additional context (screen, flow, etc.)
   * @returns {Object} WhatsApp JSON format
   */
  transformToWhatsApp(properties, context = {}) {
    if (this._transformToWhatsApp) {
      return this._transformToWhatsApp(properties, context);
    }

    // Default implementation: return type + all properties
    return {
      type: this.type,
      ...properties
    };
  }

  /**
   * Transform WhatsApp Flow JSON format to internal format
   * @param {Object} whatsappComponent - WhatsApp JSON component
   * @param {Object} context - Additional context
   * @returns {Object} Internal properties format
   */
  transformFromWhatsApp(whatsappComponent, context = {}) {
    if (this._transformFromWhatsApp) {
      return this._transformFromWhatsApp(whatsappComponent, context);
    }

    // Default implementation: extract all properties except type
    const { type, ...properties } = whatsappComponent;
    return properties;
  }

  /**
   * Lifecycle hook: Called when component is added to canvas
   * @param {Object} component - The added component
   * @param {Object} screen - The screen it was added to
   * @param {Object} context - Additional context
   */
  onAdd(component, screen, context = {}) {
    if (this._onAdd) {
      this._onAdd(component, screen, context);
    }
  }

  /**
   * Lifecycle hook: Called when component is removed from canvas
   * @param {Object} component - The removed component
   * @param {Object} screen - The screen it was removed from
   * @param {Object} context - Additional context
   */
  onRemove(component, screen, context = {}) {
    if (this._onRemove) {
      this._onRemove(component, screen, context);
    }
  }

  /**
   * Lifecycle hook: Called when a property changes
   * @param {string} propertyName - Name of changed property
   * @param {*} oldValue - Previous value
   * @param {*} newValue - New value
   * @param {Object} context - Additional context
   */
  onPropertyChange(propertyName, oldValue, newValue, context = {}) {
    if (this._onPropertyChange) {
      this._onPropertyChange(propertyName, oldValue, newValue, context);
    }
  }

  /**
   * Get a human-readable description of this component
   * @returns {string}
   */
  getDescription() {
    return this.description || `${this.name} (${this.type})`;
  }

  /**
   * Check if this component can be added to a screen
   * @param {Object} screen - The screen to check
   * @returns {boolean}
   */
  canAddToScreen(screen) {
    if (this.maxPerScreen === null || this.maxPerScreen === undefined) {
      return true; // Unlimited
    }

    const currentCount = screen.components.filter(c => c.type === this.type).length;
    return currentCount < this.maxPerScreen;
  }

  /**
   * Get error message if component cannot be added to screen
   * @param {Object} screen - The screen to check
   * @returns {string|null}
   */
  getAddBlockerMessage(screen) {
    if (this.canAddToScreen(screen)) {
      return null;
    }

    return `Maximum ${this.maxPerScreen} ${this.name} component(s) allowed per screen`;
  }
}
